import { Component, Inject, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { MatMenuTrigger, MatMenu, MatMenuItem } from '@angular/material/menu';
import { AuthService } from '@auth0/auth0-angular';

import { BrandService } from '@services/brand/1.0/brand.service';
import { DEFAULT_LANGUAGE } from '@modules/i18n/1.0';
import { Tenant } from '@generated/tcea/models/tenant';
import { MatSelectChange, MatSelect } from '@angular/material/select';
import {
  UntypedFormBuilder,
  UntypedFormControl,
  UntypedFormGroup,
  Validators,
  FormsModule,
  ReactiveFormsModule,
} from '@angular/forms';
import { AppConfigService } from '@services/app-config/1.0/app-config.service';
import { TRANSLATIONS } from 'assets/i18n';
import { ButtonStyle } from '@constants/button/1.0';
import { PermissionsService } from '@services/permissions/1.0/permissions.service';
import { Subscription } from 'rxjs';
import { CallCenterModeService } from '@services/call-center/call-center-mode.service';
import { CallCenterCallTimerService } from '@services/call-center/call-center-call-timer.service';
import { ResellerModeService } from '@services/reseller/reseller-mode.service';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { ConfirmationComponent } from '@components/confirmation/1.0';
import {
  CallCenterOrderSetupService,
  OrderStep,
} from '@services/call-center/call-center-order-setup.service';
import { TranslateService, TranslateModule } from '@ngx-translate/core';
import { environment } from 'environments/environment';
import { HeaderComponent } from '../../header/1.0/header.component';
import { NgClass, NgIf, NgFor, AsyncPipe } from '@angular/common';
import { ButtonComponent } from '../../../components/button/1.0/button.component';
import { MatTooltip } from '@angular/material/tooltip';
import { MatOption } from '@angular/material/core';
import { BrandDetailComponent } from '../../../../app/brand/brand-dashboard/brand-detail/brand-detail.component';

@Component({
  selector: 'txp-app-header',
  templateUrl: './app-header.component.html',
  styleUrls: ['./app-header.component.scss'],
  host: { class: 'txp-app-header' },
  standalone: true,
  imports: [
    HeaderComponent,
    MatMenuTrigger,
    NgClass,
    NgIf,
    ButtonComponent,
    MatTooltip,
    FormsModule,
    ReactiveFormsModule,
    MatSelect,
    NgFor,
    MatOption,
    MatMenu,
    MatMenuItem,
    BrandDetailComponent,
    TranslateModule,
    AsyncPipe,
  ],
})
export class AppHeaderComponent implements OnInit, OnDestroy {
  @Input() logoImageSrc: string;
  @Input() logoRouterLink = '/';
  @Input() logoAlt = '';
  @Input() brandName: string;
  @ViewChild(MatMenuTrigger) trigger: MatMenuTrigger;

  buttonStyle = ButtonStyle;

  brand: Tenant;
  brandId: string;
  form: UntypedFormGroup;
  supportedLanguages: string[];
  menuOpen: boolean;
  user: any;
  showBrandList: boolean;
  brandListCount = 0;
  isCallCenterModeAvailable = false;
  isCallCenterModeActive = false;
  callCenterId: string;
  isCallCenterTimerStarted = false;
  currentCallTime = '0:00';
  orderStep = OrderStep;
  callCenterEnabled = false;
  isResellerModeActive = false;
  isResellerAvailable = false;
  showResellerFilter = false;
  resellerLogoUrl: string | null = null;
  isSuperAdmin = false;

  orderStep$;
  environment = environment;

  private subscriptions: Subscription = new Subscription();

  constructor(
    private appConfig: AppConfigService,
    private formBuilder: UntypedFormBuilder,
    private authService: AuthService,
    private brandService: BrandService,
    private permissionsService: PermissionsService,
    private callCenterModeService: CallCenterModeService,
    private callCenterCallTimerService: CallCenterCallTimerService,
    private callCenterOrderSetupService: CallCenterOrderSetupService,
    private resellerModeService: ResellerModeService,
    private dialog: MatDialog,
    private router: Router,
    private translateService: TranslateService,
    @Inject(DEFAULT_LANGUAGE) private defaultLanguageCode: any,
  ) {
    this.form = this.formBuilder.group({
      language: new UntypedFormControl('', Validators.required),
    });

    // Subscribe to timer value
    this.subscriptions.add(
      this.callCenterCallTimerService.timer$.subscribe((time) => {
        this.currentCallTime = time;
      }),
    );

    // Subscribe to running status
    this.subscriptions.add(
      this.callCenterCallTimerService.isRunning$.subscribe((isRunning) => {
        this.isCallCenterTimerStarted = isRunning;
      }),
    );

    this.orderStep$ = this.callCenterOrderSetupService.orderStep$;

    // Subscribe reseller availability (from PermissionsService initialization)
    this.resellerModeService.getIsAvailable().subscribe((available) => {
      this.isResellerAvailable = available;
    });

    // React to reseller mode changes to adjust header visuals
    this.resellerModeService.getIsActive().subscribe((isActive) => {
      this.isResellerModeActive = isActive;
      if (isActive) {
        this.brandName = this.translateService.instant('app.nav.resellerManagement');
        this.showBrandList = false;
      } else {
        // Restore brand header when exiting reseller mode
        this.brandName = this.brand?.name || 'YOUR BRANDS';
        this.showBrandList = this.brandListCount > 1;
      }
    });
  }

  ngOnInit() {
    // Check if user is Super Admin after permissions are loaded
    this.subscriptions.add(
      this.permissionsService.loaded$.subscribe((loaded) => {
        if (loaded) {
          this.isSuperAdmin = this.permissionsService.getRole('SuperAdmin');
        }
      }),
    );

    this.brandService.currentBrandBehaviorSubject.subscribe((brand) => {
      if (brand?.isSet) {
        this.brand = brand;
        this.callCenterEnabled =
          brand.aggregatorConfigurations?.['consumer-api']?.orderingChannels?.['call_center']
            ?.enabled ?? false;
        const languageCodes = Object.keys(TRANSLATIONS);
        // selected by user, default language, browser language
        const selectedLanguage = this.defaultLanguageCode || brand.language || navigator.language;
        const defaultLanguage = languageCodes.includes(selectedLanguage)
          ? selectedLanguage
          : this.appConfig.settings.defaultLanguage;

        this.form.patchValue({
          language: defaultLanguage,
        });

        this.supportedLanguages = languageCodes;

        this.callCenterModeService.getIsActive().subscribe((isActive) => {
          this.isCallCenterModeActive = isActive;
        });

        this.resellerModeService.getIsActive().subscribe((isActive) => {
          this.isResellerModeActive = isActive;
        });

        this.isCallCenterModeAvailable =
          this.permissionsService.getPermission('call-centers:write');
      }

      this.brandId = brand?.id;
      if (!this.isResellerModeActive) {
        this.brandName = brand?.name || 'YOUR BRANDS';
      }
    });

    this.brandService.currentBrandListBehaviorSubject.subscribe((tenants) => {
      this.brandListCount = tenants.length;
      this.showBrandList = tenants.length > 1;
    });

    // Subscribe to reseller filter visibility
    this.subscriptions.add(
      this.brandService.showResellerFilter$.subscribe((shouldShow) => {
        this.showResellerFilter = shouldShow;
      }),
    );

    // Subscribe to reseller logo URL from cache
    this.subscriptions.add(
      this.brandService.resellerLogoUrl$.subscribe((logoUrl) => {
        this.resellerLogoUrl = logoUrl;
      }),
    );

    // Watch for changes to the profile data
    this.authService.user$.subscribe((user) => {
      this.user = user;
    });
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  onChangeLanguage(selection: MatSelectChange) {
    if (selection) {
      localStorage.setItem('defaultLanguage', selection.value);
      location.reload();
    }
  }

  openMenu() {
    this.trigger.openMenu();
  }

  setAppMode(isCallCenter: boolean) {
    this.callCenterModeService.setMode(isCallCenter);
  }

  setResellerMode(isReseller: boolean) {
    this.resellerModeService.setMode(isReseller);
    if (isReseller) {
      // Navigate to reseller root when activating reseller mode
      this.router.navigateByUrl('/reseller');
    } else {
      // When leaving reseller mode, return to brand page
      this.router.navigateByUrl('/brand');
    }
  }

  get isXpAppAvailable(): boolean {
    const isProduction = environment.production || environment.name?.startsWith('production');
    return !!this.brandId && (!isProduction || this.isSuperAdmin);
  }

  // Whether the app switcher (Tillster grid) should be available (arrow shown, menu enabled)
  get isAppMenuAvailable(): boolean {
    const callCenterAvailable =
      !!this.brandId && this.callCenterEnabled && this.isCallCenterModeAvailable;
    return this.isResellerAvailable || callCenterAvailable || this.isXpAppAvailable;
  }

  endCall() {
    const dialogRef = this.dialog.open(ConfirmationComponent, {
      data: {
        title: this.translateService.instant('callCenter.dashboard.endCall.title'),
        message: this.translateService.instant('callCenter.dashboard.endCall.message'),
        cancelText: this.translateService.instant('form.button.cancel'),
        confirmText: this.translateService.instant('form.button.yes'),
      },
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        this.callCenterOrderSetupService.setOrderStep(OrderStep.Disposition);
        const currentUrl = this.router.url.split('?')[0];
        if (!currentUrl.includes('/call-center/app/dashboard')) {
          const ANI = this.callCenterOrderSetupService.getANI();
          const DNIS = this.callCenterOrderSetupService.getDNIS();

          this.callCenterModeService.setMode(true, false);
          this.router.navigateByUrl('/call-center/app/dashboard', {
            state: { ANI, DNIS },
          });
        }
      }
    });
  }

  openXpApp() {
    // Only restrict to Super Admin users in production environment
    const isProduction = environment.production || environment.name?.startsWith('production');
    if (isProduction && !this.isSuperAdmin) {
      console.warn(
        '[AppHeaderComponent] Access denied: XP app is restricted to Super Admin users in production',
      );
      return;
    }
    this.openExternalApp(EXTERNAL_ROUTES.xp);
  }

  private openExternalApp(baseUrl: string) {
    this.authService.getAccessTokenSilently().subscribe((token) => {
      const params = new URLSearchParams({ brandId: this.brandId, auth: token });
      window.open(`${baseUrl}?${params.toString()}`, '_blank');
    });
  }

  onBrandMenuOpened() {
    // Wait for the next tick to ensure the panel is rendered
    setTimeout(() => {
      // Find the menu panel element - it's created in the overlay
      const menuPanel = document.querySelector('.brand-menu.mat-mdc-menu-panel') as HTMLElement;
      if (menuPanel) {
        if (this.showResellerFilter) {
          menuPanel.classList.add('has-reseller-filter');
        } else {
          menuPanel.classList.remove('has-reseller-filter');
        }
      }
    }, 0);
  }
}

const EXTERNAL_ROUTES = {
  get xp() {
    return (environment as any).txpAdminUrl;
  },
};
